import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Search } from 'basic-components';
import { ActiveCallsIcon, AddRow, CurvesIcon, FilesIcon, SeriesIcon } from 'basic-components/icons';
import { CommonService } from 'dashboard-services/services';
import { globalActions } from 'primary-components';

import { routing } from 'routing';
import { AuthUtils, CurvesUtils, ExcelUtils, FileUtils, GAUtils, TimeSeriesUtils } from 'utils';

import EmptyList from 'primary/EmptyList';
import HeaderNoSearch from 'primary/HeaderNoSearch';

import CallSection from './CallSection';
import CurveDatapointRow from './CurveDatapointRow';
import CurveRow from './CurveRow';
import FileRow from './FileRow';
import SerieDatapointRow from './SerieDatapointRow';
import SerieRow from './SerieRow';

import { When } from 'react-if';

import './Active.scss';

const validateTable = async ({ item = [], cellValue, cell, formula }) => {
  let localItem = await TimeSeriesUtils.validateTable({ item, cellValue, cell, formula })
  localItem = await FileUtils.validateTable({ item: localItem, cellValue, cell, formula })
  localItem = await CurvesUtils.validateTable({ item: localItem, cellValue, cell, formula })
  return localItem;
}
const REFRESH_PREFIX = "ActiveCalls_Refresh_"
const Active = () => {
  const [query, setQuery] = useState(),
        [activeCalls, setActiveCalls] = useState([]),
        dispatch = useDispatch(),
        isFetching = useSelector(state => state.globalState.fetchingRequests.filter(f => f.id?.startsWith(REFRESH_PREFIX)).length > 0),
        isLoadingRef = useRef(false),
        loadActiveCalls = useCallback(async () => {
          if(isLoadingRef.current) {
            return Promise.resolve()
          }
          isLoadingRef.current = true;
          const requestId = REFRESH_PREFIX + CommonService.generateUID()
          dispatch(globalActions.handleRequest({ id: requestId }))
          return ExcelUtils.getActiveCallsFromSpreadSheets({ validateTable })
            .then(items => items.filter(item => item !== undefined))
            .then(items => setActiveCalls(items))
            .catch(err => {
              dispatch(globalActions.getMessageStore()).error(String(err))
              console.error(String(err))
              dispatch(AuthUtils.sendError({ error: "(ActiveCalls)" + err.stack }))
            })
            .finally(() => {
              isLoadingRef.current = false;
              dispatch(globalActions.handleRequest({ id: requestId }))
            })
        }, [dispatch]),
        isMatchingQuery = useCallback((value = "") => {
          const localQuery = String(query || "").trim().toUpperCase()
          if(!localQuery || localQuery === "") {
            return true;
          }
          return value.toUpperCase().match(localQuery)
        }, [query]),
        serieItems = useMemo(() => 
          activeCalls
            .filter(a => a.prefix === TimeSeriesUtils.TS_PARAMS_PREFIX)
            .filter(a => a.isDatapoint !== true)
            .filter(a => {
              const groupedParams = a.parsedParameters.getGroupedParams()
              return groupedParams.some(gp => {
                if(isMatchingQuery(gp.groupName)) {
                  return true
                }
                return (
                  Object.values(gp.symbols || {}).some(v => isMatchingQuery(v)) ||
                  Object.values(gp.metadatas || {}).some(v => isMatchingQuery(v))
                )
              })
            }),
        [activeCalls, isMatchingQuery]),
        serieDatapointItems = useMemo(() => 
          activeCalls
            .filter(a => a.prefix === TimeSeriesUtils.TS_PARAMS_PREFIX)
            .filter(a => a.isDatapoint === true)
            .filter(a => {
              const p = a.parsedParameters || {}
              return (
                isMatchingQuery(p.groupName) ||
                isMatchingQuery(p.symbol) || 
                isMatchingQuery(p.column) || 
                isMatchingQuery(p.source) || 
                isMatchingQuery(p.date) 
              )
            })
        , [activeCalls, isMatchingQuery]),
        curveItems = useMemo(() => activeCalls.filter(a => a.prefix === CurvesUtils.FC_PARAMS_PREFIX).filter(a => a.isDatapoint !== true).filter(a => isMatchingQuery(a.product) || isMatchingQuery(a.root) || isMatchingQuery(a.groupName)), [activeCalls, isMatchingQuery]),
        curveDatapointItems = useMemo(() => activeCalls.filter(a => a.prefix === CurvesUtils.FC_PARAMS_PREFIX).filter(a => a.isDatapoint === true).filter(a => isMatchingQuery(a.product) || isMatchingQuery(a.root) || isMatchingQuery(a.groupName)), [activeCalls, isMatchingQuery]),
        fileItems = useMemo(() => activeCalls.filter(a => a.prefix === FileUtils.FILE_PREFIX).filter(a => isMatchingQuery(a.fileName) || isMatchingQuery(a.groupName)), [activeCalls, isMatchingQuery]),
        refreshCall = useCallback(({ formula, address }) => {
          GAUtils.sendEvent({
            category: "action",
            action: "refresh_call",
            label: "refresh"
          })
          return Excel.run(context => 
            ExcelUtils
              .refreshFormulaWithAdress({ address, formula, context })
              .catch(err => {
                dispatch(AuthUtils.sendError({ error: "(RefreshCall)" + err.stack }))
                dispatch(globalActions.getMessageStore()).error(String(err))
                console.error(String(err))
                return Promise.resolve()
              })
              .finally(() => loadActiveCalls())
          )
        }, [dispatch, loadActiveCalls]),
        clearTable = useCallback(async address => {
          await ExcelUtils.clearTableWithFormulaAddress(address)
          loadActiveCalls()
        }, [loadActiveCalls])

  useEffect(() => {
    if(query) {
      GAUtils.sendEvent({
        category: "search",
        action: "search_active",
        label: "search"
      })
    }
  }, [query])

  useEffect(() => {
    GAUtils.sendEvent({
      category: "open",
      action: "open_active",
      label: "open"
    })
    GAUtils.sendPageView(routing.ACTIVE.name)
  }, [])

  useEffect(() => {
    loadActiveCalls()
  }, [loadActiveCalls])

  return (
    <div className="ng-office-app__authed__content ng-office-app__authed__content--active">
      <HeaderNoSearch
          icon={(
            <ActiveCallsIcon
                color="grey-dark"
                height={12}
                width={12}
            />
          )}
          title="ACTIVE CALLS"
      >
        <div className="ng-office-app__authed__content__search">
          <Search
              defaultValue={query}
              onSearch={setQuery}
              placeholder="Search in active calls"
              size="large"
              variant="outline"
          />
        </div>
      </HeaderNoSearch>
      <div className="ng-office-app__authed__content__active-calls">
        <CallSection
            icon={<SeriesIcon color="grey-dark"/>}
            itemsCount={serieItems.length + serieDatapointItems.length}
            title="SERIES"
        >
          <CallSection
              expandedByDefault
              icon={<SeriesIcon color="grey-dark"/>}
              isInner
              itemsCount={serieItems.length}
              title="BATCH CALLS"
          >
            {serieItems.map((item, index) => (
              <SerieRow
                  {...item}
                  clearTable={clearTable}
                  key={index}
                  refreshCall={refreshCall}
              />
            ))}
          </CallSection>
          <CallSection
              icon={<AddRow color="grey-dark"/>}
              isInner
              itemsCount={serieDatapointItems.length}
              title={"SINGLE DATA POINT CALLS"}
          >
            {serieDatapointItems.map((item, index) => (
              <SerieDatapointRow
                  {...item}
                  clearTable={clearTable}
                  key={index}
                  refreshCall={refreshCall}
              />
            ))}
          </CallSection>
        </CallSection>
        <CallSection
            icon={<CurvesIcon color="grey-dark"/>}
            itemsCount={curveItems.length + curveDatapointItems.length}
            title="CURVES"
        >
          <CallSection
              expandedByDefault
              icon={<CurvesIcon color="grey-dark"/>}
              isInner
              itemsCount={curveItems.length}
              title={"BATCH CALLS"}
          >
            {curveItems.map((item, index) => (
              <CurveRow
                  {...item}
                  clearTable={clearTable}
                  key={index}
                  refreshCall={refreshCall}
              />
            ))}
          </CallSection>
          <CallSection
              icon={<AddRow color="grey-dark"/>}
              isInner
              itemsCount={curveDatapointItems.length}
              title={"SINGLE DATA POINT CALLS"}
          >
            {curveDatapointItems.map((item, index) => (
              <CurveDatapointRow
                  {...item}
                  clearTable={clearTable}
                  key={index}
                  refreshCall={refreshCall}
              />
            ))}
          </CallSection>
        </CallSection>
        <CallSection
            icon={<FilesIcon color="grey-dark"/>}
            itemsCount={fileItems.length}
            title="FILES"
            withPadding
        >
          {fileItems.map((item, index) => (
            <FileRow
                {...item}
                clearTable={clearTable}
                key={index}
                refreshCall={refreshCall}
            />
          ))}
        </CallSection>
        <When condition={activeCalls.length === 0 && !isFetching}>
          {() => (
            <EmptyList
                subtitle="No active calls."
                title="Create data calls in Series, Curve or Files."
            />
          )}
        </When>
      </div>
    </div>
  )
}

export default Active;